/**
 * A Dashboard card the device cannot answer from its own rows (owner
 * decision, 29 Oct 2026): asked of the server, kept, and shown with the time
 * it was worked out.
 *
 * The device keeps about ninety days of rows and, further back, day buckets
 * that know one sum per record per day. A card that reaches below the floor
 * of a record it reads and that no bucket can answer — Pending across Jobs
 * and Received this year — used to draw a quiet "Nothing yet". Now:
 *
 *   1. `asksServer` (shared) decides, from the card and its floor, whether
 *      only the server can answer it. Nothing else is ever asked.
 *   2. A kept answer is shown at once, on every open, offline included.
 *   3. It is asked again when it is older than fifteen minutes, on another
 *      day, or after a sync moved any record the card reads
 *      (`keptAnswerFresh`). A failed ask keeps the last answer and waits a
 *      minute before the next; with none kept, the quiet placeholder stays.
 *      A refusal is not a failure: the server said this person may not see
 *      that figure (a hidden field, a record they cannot read), so the kept
 *      answer goes and the card shows the quiet placeholder.
 *      A kept answer whose period has ended — "today" after midnight, "this
 *      month" into the next — is not drawn: the card shows the quiet
 *      placeholder until the new period's answer lands.
 *   4. An answer is one person's: kept under the signed-in user, never asked
 *      with nobody signed in, and dropped when it lands after a sign-out.
 *
 * Every card then says how fresh its figure is: "Worked out at 10:40" for a
 * server answer, "Synced at 10:42" for a card the device answered from rows
 * the last sync brought (`cardStampLine`).
 *
 * The database and the network come in through `AnswerSeam`, so this file is
 * tested under bun with neither.
 */
import {
  asksServer,
  formatClock,
  formatStamp,
  keptAnswerFresh,
  serverWindowOf,
  t,
  type InsightAnswerRequest,
  type InsightAnswerResponse,
  type InsightResult,
  type InsightSpec,
  type ServerInsightSpec,
} from '@alluzo/shared';

/** A server answer as the device keeps it. */
export type KeptAnswer = {
  readonly result: InsightResult;
  /** When the server worked it out. */
  readonly computedAt: string;
  /** The workspace's day it was asked on: "this month" moves at midnight. */
  readonly day: string;
  /** `cardReadsSignature` when it was asked: what the sync had brought of the records it reads. */
  readonly reads: string;
};

/** How fresh a card's figure is: worked out on the server, or answered from rows a sync brought. */
export type CardStamp = { readonly kind: 'worked_out' | 'synced'; readonly at: string };

/** What this file needs of the device. */
export type AnswerSeam = {
  /** The latest floor among the records a card reads (`cardFloorDay`). */
  readonly floorOf: (host: string, spec: InsightSpec) => string | null;
  readonly readsOf: (host: string, spec: InsightSpec) => string;
  readonly kept: (key: string) => string | null;
  readonly keep: (key: string, value: string) => void;
  /** Drops a kept answer: the server refused the card. */
  readonly forget: (key: string) => void;
  readonly ask: (body: InsightAnswerRequest) => Promise<InsightAnswerResponse>;
};

/** A card the server answers: where its answer is kept, the answer, and whether it is fresh. */
export type ServerCard = {
  /** Null with nobody signed in: such a card is never asked and shows nothing. */
  readonly key: string | null;
  readonly host: string;
  readonly spec: ServerInsightSpec;
  readonly kept: KeptAnswer | null;
  readonly fresh: boolean;
  readonly reads: string;
};

/** JSON with its keys in one order, so a card asked twice is kept once. */
const canonical = (value: unknown): string => {
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  if (value !== null && typeof value === 'object') {
    const keys = Object.keys(value as Record<string, unknown>)
      .filter((key) => (value as Record<string, unknown>)[key] !== undefined)
      .sort();
    return `{${keys.map((key) => `${JSON.stringify(key)}:${canonical((value as Record<string, unknown>)[key])}`).join(',')}}`;
  }
  return JSON.stringify(value);
};

/** The `sync_meta` key a card's answer is kept under: the business, the person, the host and what it asks. */
export const answerKeyOf = (tenantId: string, userId: string, host: string, spec: InsightSpec): string =>
  `insight_answer:${tenantId}:${userId}:${host}:${canonical(spec)}`;

const readKept = (raw: string | null): KeptAnswer | null => {
  if (raw === null) return null;
  try {
    const held = JSON.parse(raw) as Partial<KeptAnswer>;
    if (typeof held.computedAt !== 'string' || typeof held.day !== 'string' || typeof held.reads !== 'string') return null;
    if (held.result === null || typeof held.result !== 'object') return null;
    return held as KeptAnswer;
  } catch {
    return null;
  }
};

/**
 * Whether the period an answer kept on `keptDay` covered is over by `today`:
 * "today" asked yesterday, "this month" asked last month. Its figure is then
 * another period's, not an older figure for this one.
 */
const periodEnded = (spec: InsightSpec, keptDay: string, today: string): boolean => {
  if (keptDay === today) return false;
  const then = serverWindowOf(spec, keptDay);
  const current = serverWindowOf(spec, today);
  if (!then.ok || !current.ok) return true;
  return then.window.from !== current.window.from || then.window.to !== current.window.to;
};

/**
 * Whether only the server can answer this card, and if so what is kept for
 * it. Null for every card the device answers itself. A kept answer whose
 * period has ended reads as none kept.
 */
export const serverCardOf = (
  seam: AnswerSeam,
  tenantId: string,
  userId: string | null,
  host: string,
  spec: InsightSpec,
  today: string,
  now: Date,
): ServerCard | null => {
  if (!asksServer(spec, today, seam.floorOf(host, spec))) return null;
  const key = userId === null ? null : answerKeyOf(tenantId, userId, host, spec);
  const held = key === null ? null : readKept(seam.kept(key));
  const kept = held !== null && periodEnded(spec, held.day, today) ? null : held;
  const reads = seam.readsOf(host, spec);
  return {
    key,
    host,
    spec: spec as ServerInsightSpec,
    kept,
    reads,
    fresh: kept !== null && keptAnswerFresh(kept, { at: now, day: today, reads }),
  };
};

/** A minute between failed asks of one card, so an offline Dashboard does not knock every render. */
export const ASK_AGAIN_AFTER_FAILURE_MS = 60_000;

const inFlight = new Set<string>();
const failedAt = new Map<string, number>();
/** Moves on at every sign-out, so an ask that was in flight then keeps nothing. */
let generation = 0;

/** Forgets the failures and asks in flight. Tests only. */
export const resetAnswerAsks = (): void => {
  inFlight.clear();
  failedAt.clear();
};

/**
 * Sign-out (`forgetDevice`): every ask in flight is dropped when it lands, and
 * the next person starts with no failures held against their cards.
 */
export const forgetAnswerAsks = (): void => {
  generation += 1;
  resetAnswerAsks();
};

/** Statuses that say "not now" rather than "not you": the next ask may well be answered. */
const TRANSIENT = new Set([401, 408, 429]);

/**
 * Whether the server refused the card — a 4xx carrying its own code, such as
 * FIELD_HIDDEN or LIST_MONEY_NOT_TOTALLED — rather than failing to answer it.
 * A network failure, a 5xx (a timeout is a 503) or a bare status is a failure.
 */
export const isRefusal = (error: unknown): boolean => {
  if (error === null || typeof error !== 'object') return false;
  const { status, code } = error as { status?: unknown; code?: unknown };
  return typeof status === 'number' && status >= 400 && status < 500 && !TRANSIENT.has(status) && typeof code === 'string' && code !== '';
};

/**
 * Asks the server again for a card whose kept answer is not fresh, and keeps
 * what it says. True when what the card shows changed: a new answer kept, or
 * a kept one dropped because the server refused the card. False when nothing
 * was asked or the ask failed, and then the last kept answer stands.
 */
export const refreshServerCard = async (
  seam: AnswerSeam,
  card: ServerCard,
  today: string,
  zone: string | null,
  now: Date,
): Promise<boolean> => {
  const key = card.key;
  if (key === null || card.fresh || inFlight.has(key)) return false;
  const failed = failedAt.get(key);
  if (failed !== undefined && now.getTime() - failed < ASK_AGAIN_AFTER_FAILURE_MS) return false;
  const asked = generation;
  inFlight.add(key);
  try {
    // The device's own day goes with the ask, so the server reads "today" and
    // "this month" on the day the device keeps the answer under.
    const answer = await seam.ask({ recordId: card.host, spec: card.spec, today, ...(zone === null ? {} : { zone }) });
    if (generation !== asked) return false;
    const kept: KeptAnswer = { result: answer.result, computedAt: answer.computedAt, day: today, reads: card.reads };
    seam.keep(key, JSON.stringify(kept));
    failedAt.delete(key);
    return true;
  } catch (error) {
    if (generation !== asked) return false;
    failedAt.set(key, now.getTime());
    if (isRefusal(error)) {
      seam.forget(key);
      return card.kept !== null;
    }
    return false;
  } finally {
    if (generation === asked) inFlight.delete(key);
  }
};

/** A card's figure from the server, or null while none is kept: the quiet placeholder. */
export const serverResultOf = (card: ServerCard): InsightResult | null => card.kept?.result ?? null;

/** The stamp of a card the server answered, or null while no answer is kept. */
export const serverStampOf = (card: ServerCard): CardStamp | null =>
  card.kept === null ? null : { kind: 'worked_out', at: card.kept.computedAt };

/** The stamp of a card answered on the device, from the last successful sync. */
export const syncedStampOf = (lastSyncedAt: string | null): CardStamp | null =>
  lastSyncedAt === null ? null : { kind: 'synced', at: lastSyncedAt };

/**
 * The card's time line: "Worked out at 10:40 am" or "Synced at 10:42 am",
 * with the date in front of the time when it was not today.
 */
export const cardStampLine = (stamp: CardStamp, now: Date): string => {
  const instant = new Date(stamp.at);
  if (Number.isNaN(instant.getTime())) return '';
  const time = formatClock(stamp.at);
  const today =
    instant.getFullYear() === now.getFullYear() && instant.getMonth() === now.getMonth() && instant.getDate() === now.getDate();
  if (today) return stamp.kind === 'worked_out' ? t('insight.stamp.worked_out', { time }) : t('insight.stamp.synced', { time });
  const date = formatStamp(stamp.at, now);
  return stamp.kind === 'worked_out' ? t('insight.stamp.worked_out_on', { date, time }) : t('insight.stamp.synced_on', { date, time });
};
